import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown, Phone, Menu, X } from "lucide-react";

const SECONDARY_LINKS = [
  { label: "Contact Us", href: "https://www.skytpo.com/contact-us" },
  { label: "About Us", href: "https://www.skytpo.com/about-us" },
  { label: "Blog", href: "https://www.skytpo.com/blog" },
];
const DROPDOWN_CONTENT = {
  "Loan Products": [
    { label: "Rental Income Loan (1-8 Units)", href: "https://www.skytpo.com/loan-products/investor-dscr-loans" },
    { label: "No-Income Verification Home Loan", href: "https://www.skytpo.com/loan-products/non-qm-loans" },
    { label: "Deposit-Based Loan", href: "https://www.skytpo.com/loan-products/self-employed-mortgage-alt-doc" },
    { label: "Asset-Based Loans", href: "https://www.skytpo.com/loan-products/asset-based-loans" },
    { label: "Super Jumbo & Elite Mortgage Loans", href: "https://www.skytpo.com/loan-products/super-jumbo-elite-loans" },
  ],
  Branches: [
    { label: "Las Vegas, NV", href: "https://www.skytpo.com/branch/las-vegas-branch-office" },
    { label: "Palmdale, CA", href: "https://www.skytpo.com/branch/palmdale-branch-office" },
    { label: "San Fernando, CA", href: "https://www.skytpo.com/branch/san-fernando-branch-office" },
    { label: "Costa Mesa, CA", href: "https://www.skytpo.com/branch/costa-mesa-branch-office" },
    { label: "Melville, NY", href: "https://www.skytpo.com/branch/melville-branch-office" },
  ],
  Calculators: [
    { label: "Mortgage Payment", href: "https://www.skytpo.com/mortgage-payment-calculator" },
    { label: "DSCR", href: "https://www.skytpo.com/dscr-calculator" },
    { label: "Refinance", href: "https://www.skytpo.com/refinance-calculator" },
    { label: "Affordability", href: "https://www.skytpo.com/affordability-calculator" },
  ],
  Resources: [
    { label: "About", href: "https://www.skytpo.com/about" },
    { label: "Products", href: "https://www.skytpo.com/products" }
  ],
};

const MAIN_LINKS = [
  { label: "Resources", href: "/loan-products", hasDropdown: true }
];

const PHONE_DISPLAY = "(818) 479-8770";
const PHONE_HREF = "tel:+18184798770";
const APPLY_HREF = "https://apply.skytpo.com/";
const LOGO_SRC = "content/Images/mmc/SkyPORTLogo.png";

export default function MMCLendingHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  const toggleDropdown = (label) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  // Keep mouse clicks from leaving focus (and the browser focus ring) on links.
  // Keyboard focus is unaffected, so focus-visible styles still work.
  const preventFocusOnClick = (e) => e.preventDefault();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="w-full border-b border-slate-200 bg-white relative z-[1000000]">
      <div className="max-w-8xl mx-auto px-4">
        <div className="lg:grid lg:grid-cols-[auto_1fr] lg:grid-rows-[auto_auto]">
          {/* Logo */}
          <a
            href="https://www.skytpo.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Sky TPO - Go to homepage"
            onMouseDown={preventFocusOnClick}
            className="row-span-2 flex items-center pr-8 !no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline focus:outline-none"
          >
            <img
              src={LOGO_SRC}
              alt="Sky TPO Logo"
              className="mt-2 h-24 w-[350px] object-contain object-left  no-underline"
            />
          </a>



          {/* Main nav */}
          <div className="flex items-center justify-end gap-8 h-16">
            <nav className="flex items-center space-x-1 mb-4" aria-label="Main navigation">
              {MAIN_LINKS.map((link) => {
                const isOpen = openDropdown === link.label;
                const items = DROPDOWN_CONTENT[link.label];
                return (
                  <div className="relative z-[110] font-medium" key={link.label} style={{ zIndex: 1000000 }}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-haspopup={link.hasDropdown ? "true" : undefined}
                      aria-expanded={link.hasDropdown ? isOpen : undefined}
                      onMouseDown={preventFocusOnClick}
                      onClick={
                        link.hasDropdown
                          ? (e) => {
                            e.preventDefault();
                            toggleDropdown(link.label);
                          }
                          : undefined
                      }
                      className={`flex items-center px-4 py-2 text-lg font-medium transition-colors !no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline cursor-pointer focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${isOpen
                        ? "text-[#0E2A47] bg-slate-100"
                        : "text-[#0E2A47] hover:text-[#0E2A47] hover:bg-slate-100"
                        }`}
                    >
                      {link.label}
                      {link.hasDropdown && (
                        <ChevronDown
                          size={16}
                          aria-hidden="true"
                          className={`ml-1 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                      )}
                    </a>

                    {/* Dropdown panel */}
                    {link.hasDropdown && isOpen && items && (
                      <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-lg py-2" style={{ zIndex: 1001 }}>
                        {items.map((item) => (
                          <a
                            key={item.label}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2.5 text-sm text-slate-500
                                        hover:bg-slate-50 hover:text-[#295E9B]
                                        transition-colors duration-300 ease-in-out
                                        !no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline focus:outline-none"
                            onMouseDown={preventFocusOnClick}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

            </nav>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="lg:hidden">
          <div className="flex h-16 items-center justify-between">
            <a
              href="/"
              aria-label="Sky TPO - Go to homepage"
              onMouseDown={preventFocusOnClick}
              className="flex items-center space-x-2 !no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline focus:outline-none"
            >
              <img
                src={LOGO_SRC}
                alt="Sky TPO Logo"
                className="h-12 w-[140px] object-contain object-left"
              />
            </a>

            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Search site"
                className="p-2 text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
              >
                <Search size={20} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen((prev) => !prev)}
                className="inline-flex items-center justify-center rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors cursor-pointer"
              >
                {mobileOpen ? (
                  <X size={24} aria-hidden="true" />
                ) : (
                  <Menu size={24} aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile slide-down menu */}
          {mobileOpen && (
            <div className="border-t border-slate-200 pb-4">
              <nav aria-label="Mobile main navigation">
                <ul className="flex flex-col">
                  {MAIN_LINKS.map((link) => (
                    <li key={link.label} className="border-b border-slate-100">
                      <button
                        type="button"
                        onClick={() => toggleDropdown(link.label)}
                        aria-expanded={openDropdown === link.label}
                        className="w-full flex items-center justify-between px-2 py-3 text-sm font-medium text-slate-700 hover:text-teal-700 transition-colors"
                      >
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={preventFocusOnClick}
                          className="!no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline text-inherit focus:outline-none"
                        >
                          {link.label}
                        </a>
                        {link.hasDropdown && (
                          <ChevronDown
                            size={16}
                            aria-hidden="true"
                            className={`transition-transform ${openDropdown === link.label ? "rotate-180" : ""
                              }`}
                          />
                        )}
                      </button>
                    </li>
                  ))}
                  {SECONDARY_LINKS.map((link) => (
                    <li key={link.label} className="border-b border-slate-100">
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseDown={preventFocusOnClick}
                        className="block px-2 py-3 text-sm text-slate-500 hover:text-teal-700 transition-colors !no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline focus:outline-none"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="flex flex-col gap-3 px-2 pt-4">
                <a
                  href={PHONE_HREF}
                  onMouseDown={preventFocusOnClick}
                  className="flex items-center text-sm text-slate-600 !no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline focus:outline-none"
                >
                  <Phone size={16} aria-hidden="true" className="mr-2" />
                  <span className="font-medium">{PHONE_DISPLAY}</span>
                </a>
                <a
                  href={APPLY_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseDown={preventFocusOnClick}
                  className="inline-flex items-center justify-center gap-2 text-sm font-bold rounded-md bg-teal-700 text-white hover:bg-teal-800 !no-underline hover:!no-underline focus:!no-underline active:!no-underline visited:!no-underline h-10 px-4 py-2 transition-colors focus:outline-none"
                >
                  Apply Now
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
